/**
 * SmartMoneyCommandCenter — "Trung tâm Smart Money"
 *
 * Unified overview aggregating top picks from all signal engines:
 * Smart Money Score, Detector, Cross-engine Consensus, and Sector heatmap.
 */
import React, { useState, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, alpha, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Snackbar, Alert, Skeleton, Button,
} from '@mui/material';
import {
  Refresh, RocketLaunch, Diamond, Radar, Hub,
  FiberManualRecord, TrendingUp, Star,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { commandCenterAPI } from '../services/api';
import MarketContextStrip from '../components/MarketContextStrip';

// ── Config ───────────────────────────────────────────────────
const SM_LABEL_CFG = {
  STRONG_BUY: { color: '#10b981', key: 'commandCenter.smLabels.strongBuy' },
  BUY:        { color: '#3b82f6', key: 'commandCenter.smLabels.buy' },
  WATCH:      { color: '#f59e0b', key: 'commandCenter.smLabels.watch' },
  NEUTRAL:    { color: '#94a3b8', key: 'commandCenter.smLabels.neutral' },
  WEAK:       { color: '#ef4444', key: 'commandCenter.smLabels.weak' },
};

const CONS_LABEL_CFG = {
  VERY_STRONG: { color: '#10b981', key: 'commandCenter.consensusLabels.veryStrong' },
  STRONG:      { color: '#3b82f6', key: 'commandCenter.consensusLabels.strong' },
  MODERATE:    { color: '#f59e0b', key: 'commandCenter.consensusLabels.moderate' },
  WEAK:        { color: '#94a3b8', key: 'commandCenter.consensusLabels.weak' },
};

const QUAD_CFG = {
  LEADING:   { color: '#10b981', key: 'commandCenter.quadrantLabels.leading' },
  IMPROVING: { color: '#3b82f6', key: 'commandCenter.quadrantLabels.improving' },
  WEAKENING: { color: '#f59e0b', key: 'commandCenter.quadrantLabels.weakening' },
  LAGGING:   { color: '#ef4444', key: 'commandCenter.quadrantLabels.lagging' },
};

const fmtScore = (v) => v != null ? v.toFixed(1) : '—';

// ── Reusable section ─────────────────────────────────────────
const SectionHeader = ({ icon, title, count, color, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
      {icon}
      <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color }}>{title}</Typography>
      {count != null && (
        <Chip label={count} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha(color, 0.08), color }} />
      )}
    </Box>
    {action}
  </Box>
);

const hdr = { fontWeight: 700, fontSize: '0.65rem', color: '#64748b', whiteSpace: 'nowrap' };
const cell = { fontSize: '0.72rem' };

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function SmartMoneyCommandCenter() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await commandCenterAPI.get();
      setData(resp.data);
    } catch (e) {
      setError(e.message);
      setSnack({ severity: 'error', msg: t('commandCenter.messages.loadError') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => { load(); }, [load]);

  const ctx = data?.market_context;
  const smTop = data?.sm_top || [];
  const detTop = data?.det_top || [];
  const consTop = data?.consensus_top || [];
  const sectors = data?.sector_heatmap || [];
  const overlap = data?.overlap || [];

  return (
    <Box sx={{ mx: 'auto', p: { xs: 1.5, sm: 2 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RocketLaunch sx={{ fontSize: 28, color: '#6366f1' }} />
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '1.3rem', color: '#1e293b', lineHeight: 1.1 }}>
              {t('commandCenter.title')}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
              {t('commandCenter.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" size="small" startIcon={loading ? <CircularProgress size={14} /> : <Refresh />}
          onClick={load} disabled={loading}
          sx={{ fontSize: '0.72rem', textTransform: 'none', bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
          {loading ? t('commandCenter.actions.loading') : t('commandCenter.actions.refresh')}
        </Button>
      </Box>

      {/* Market Context */}
      {ctx && <MarketContextStrip summary={{ market_context: ctx }} />}

      {/* Loading skeleton matching real layout */}
      {loading && !data && (
        <Box>
          {/* Overlap skeleton */}
          <Box sx={{ mb: 2.5, p: 1.5, borderRadius: 2, border: '1px solid #f1f5f9' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
              <Skeleton variant="circular" width={18} height={18} />
              <Skeleton width={180} height={18} />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
              {[1,2,3,4,5].map(i => <Skeleton key={i} variant="rounded" width={72} height={28} sx={{ borderRadius: 1.5 }} />)}
            </Box>
          </Box>
          {/* Stacked tables skeleton */}
          {[0,1].map(sec => (
            <Box key={sec} sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.8 }}>
                <Skeleton variant="circular" width={18} height={18} />
                <Skeleton width={140} height={18} />
              </Box>
              <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <Box sx={{ bgcolor: '#f8fafc', px: 1.5, py: 0.8, display: 'flex', gap: 2 }}>
                  <Skeleton sx={{ flex: '0 0 60px' }} height={14} />
                  <Skeleton sx={{ flex: '0 0 80px' }} height={14} />
                  <Skeleton sx={{ flex: 1 }} height={14} />
                  <Skeleton sx={{ flex: '0 0 60px' }} height={14} />
                  <Skeleton sx={{ flex: '0 0 70px' }} height={14} />
                  <Skeleton sx={{ flex: '0 0 80px' }} height={14} />
                  <Skeleton sx={{ flex: '0 0 90px' }} height={14} />
                  <Skeleton sx={{ flex: '0 0 70px' }} height={14} />
                </Box>
                {Array.from({ length: sec === 0 ? 8 : 5 }).map((_, r) => (
                  <Box key={r} sx={{ px: 1.5, py: 0.7, display: 'flex', gap: 2, borderTop: '1px solid #f1f5f9' }}>
                    <Skeleton sx={{ flex: '0 0 60px' }} height={14} />
                    <Skeleton sx={{ flex: '0 0 80px' }} height={14} />
                    <Skeleton sx={{ flex: 1 }} height={14} />
                    <Skeleton sx={{ flex: '0 0 60px' }} height={14} />
                    <Skeleton sx={{ flex: '0 0 70px' }} height={14} />
                    <Skeleton sx={{ flex: '0 0 80px' }} height={14} />
                    <Skeleton sx={{ flex: '0 0 90px' }} height={14} />
                    <Skeleton sx={{ flex: '0 0 70px' }} height={14} />
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
          {/* Consensus skeleton */}
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.8 }}>
              <Skeleton variant="circular" width={18} height={18} />
              <Skeleton width={160} height={18} />
            </Box>
            <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <Box sx={{ bgcolor: '#f8fafc', px: 1.5, py: 0.8, display: 'flex', gap: 2 }}>
                <Skeleton sx={{ flex: '0 0 80px' }} height={14} />
                <Skeleton sx={{ flex: '0 0 90px' }} height={14} />
                <Skeleton sx={{ flex: 1 }} height={14} />
                <Skeleton sx={{ flex: '0 0 100px' }} height={14} />
                <Skeleton sx={{ flex: '0 0 90px' }} height={14} />
                <Skeleton sx={{ flex: '0 0 80px' }} height={14} />
                <Skeleton sx={{ flex: '0 0 70px' }} height={14} />
              </Box>
              {[1,2,3,4].map(r => (
                <Box key={r} sx={{ px: 1.5, py: 0.7, display: 'flex', gap: 2, borderTop: '1px solid #f1f5f9' }}>
                  <Skeleton sx={{ flex: '0 0 80px' }} height={14} />
                  <Skeleton sx={{ flex: '0 0 90px' }} height={14} />
                  <Skeleton sx={{ flex: 1 }} height={14} />
                  <Skeleton sx={{ flex: '0 0 100px' }} height={14} />
                  <Skeleton sx={{ flex: '0 0 90px' }} height={14} />
                  <Skeleton sx={{ flex: '0 0 80px' }} height={14} />
                  <Skeleton sx={{ flex: '0 0 70px' }} height={14} />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Overlap / Cross-engine stars ── */}
      {overlap.length > 0 && (
        <Box sx={{ mb: 2.5, p: 1.5, borderRadius: 2, bgcolor: alpha('#f59e0b', 0.04), border: `1px solid ${alpha('#f59e0b', 0.12)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
            <Star sx={{ fontSize: 18, color: '#f59e0b' }} />
            <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#92400e' }}>
              {t('commandCenter.overlap.title')}
            </Typography>
            <Chip label={overlap.length} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha('#f59e0b', 0.12), color: '#92400e' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
            {overlap.map((o) => (
              <Tooltip key={o.ticker} title={`${t('commandCenter.overlap.tooltip')}${o.sources.join(', ')}`} arrow>
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 0.3,
                  px: 0.8, py: 0.4, borderRadius: 1.5,
                  bgcolor: alpha('#f59e0b', 0.08), border: `1.5px solid ${alpha('#f59e0b', 0.2)}`,
                  cursor: 'pointer',
                }} onClick={() => navigate('/smart-money-unified')}>
                  <Star sx={{ fontSize: 10, color: '#f59e0b' }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 900, color: '#92400e' }}>{o.ticker}</Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: '#b45309' }}>×{o.sources.length}</Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
        </Box>
      )}

      {/* ── Smart Money Top (full width) ── */}
      <Box sx={{ mb: 2.5 }}>
        <SectionHeader
          icon={<Diamond sx={{ fontSize: 18, color: '#10b981' }} />}
          title={t('commandCenter.sections.smScore')} count={smTop.length} color="#10b981"
          action={<Chip label={t('commandCenter.viewAll')} size="small" clickable onClick={() => navigate('/smart-money-unified')}
            sx={{ fontSize: '0.6rem', height: 20, color: '#10b981', bgcolor: alpha('#10b981', 0.06) }} />}
        />
        {smTop.length > 0 ? (
          <TableContainer sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={hdr}>{t('commandCenter.smTable.ticker')}</TableCell>
                  <TableCell sx={hdr} align="right">{t('commandCenter.smTable.score')}</TableCell>
                  <TableCell sx={hdr}>{t('commandCenter.smTable.label')}</TableCell>
                  <TableCell sx={hdr} align="right">{t('commandCenter.smTable.mf')}</TableCell>
                  <TableCell sx={hdr} align="right">{t('commandCenter.smTable.safety')}</TableCell>
                  <TableCell sx={hdr}>{t('commandCenter.detTable.sector')}</TableCell>
                  <TableCell sx={hdr} align="right">{t('commandCenter.detTable.price')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {smTop.slice(0, 10).map((r) => {
                  const lc = SM_LABEL_CFG[r.label] || SM_LABEL_CFG.NEUTRAL;
                  return (
                    <TableRow key={r.ticker} hover sx={{ cursor: 'pointer' }} onClick={() => navigate('/smart-money-unified')}>
                      <TableCell sx={{ ...cell, fontWeight: 700 }}>{r.ticker}</TableCell>
                      <TableCell align="right" sx={{ ...cell, fontWeight: 800, fontFamily: 'monospace', color: lc.color }}>
                        {r.score.toFixed(1)}
                      </TableCell>
                      <TableCell>
                        <Chip label={t(lc.key)} size="small" sx={{ fontSize: '0.58rem', fontWeight: 700, height: 18, bgcolor: alpha(lc.color, 0.08), color: lc.color }} />
                      </TableCell>
                      <TableCell align="right" sx={{ ...cell, fontFamily: 'monospace', color: '#64748b' }}>{fmtScore(r.mf)}</TableCell>
                      <TableCell align="right" sx={{ ...cell, fontFamily: 'monospace', color: '#64748b' }}>{fmtScore(r.safety)}</TableCell>
                      <TableCell sx={{ ...cell, color: '#64748b', fontSize: '0.65rem' }}>{typeof r.sector === 'object' ? (r.sector?.[lang] || '—') : (r.sector || '—')}</TableCell>
                      <TableCell align="right" sx={{ ...cell, fontFamily: 'monospace' }}>{r.close?.toLocaleString() || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : !loading && (
          <Box sx={{ textAlign: 'center', py: 3, color: '#94a3b8', fontSize: '0.78rem' }}>{t('commandCenter.smTable.empty')}</Box>
        )}
      </Box>

      {/* ── Detector Top (full width) ── */}
      <Box sx={{ mb: 2.5 }}>
        <SectionHeader
          icon={<Radar sx={{ fontSize: 18, color: '#8b5cf6' }} />}
          title={t('commandCenter.sections.detector')} count={detTop.length} color="#8b5cf6"
          action={<Chip label={t('commandCenter.viewAll')} size="small" clickable onClick={() => navigate('/market-divergence')}
            sx={{ fontSize: '0.6rem', height: 20, color: '#8b5cf6', bgcolor: alpha('#8b5cf6', 0.06) }} />}
        />
        {detTop.length > 0 ? (
          <TableContainer sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ ...hdr, width: 32, pr: 0 }} align="center">#</TableCell>
                  <TableCell sx={{ ...hdr, pl: 0.5 }}>{t('commandCenter.detTable.ticker')}</TableCell>
                  <TableCell sx={hdr}>{t('commandCenter.detTable.quality')}</TableCell>
                  <TableCell sx={hdr}>{t('commandCenter.detTable.setup')}</TableCell>
                  <TableCell sx={hdr}>{t('commandCenter.detTable.sector')}</TableCell>
                  <TableCell sx={hdr} align="right">{t('commandCenter.detTable.price')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detTop.slice(0, 10).map((r, idx) => {
                  const qCfg = r.quality_score >= 90 ? { color: '#10b981', bg: '#ecfdf5', key: 'commandCenter.detTable.qualityExcellent' }
                    : r.quality_score >= 75 ? { color: '#3b82f6', bg: '#eff6ff', key: 'commandCenter.detTable.qualityGood' }
                    : r.quality_score >= 60 ? { color: '#f59e0b', bg: '#fffbeb', key: 'commandCenter.detTable.qualityFair' }
                    : { color: '#94a3b8', bg: '#f8fafc', key: 'commandCenter.detTable.qualityWeak' };
                  const setupKey = { OTHER: 'commandCenter.detTable.setupOther', BREAKOUT: 'commandCenter.detTable.setupBreakout', REVERSAL: 'commandCenter.detTable.setupReversal', MOMENTUM: 'commandCenter.detTable.setupMomentum' }[r.setup_type?.toUpperCase()] || null;
                  return (
                    <TableRow key={r.ticker} hover sx={{ cursor: 'pointer' }} onClick={() => navigate('/market-divergence')}>
                      <TableCell align="center" sx={{ ...cell, pr: 0, color: '#a78bfa', fontWeight: 700, fontFamily: 'monospace', fontSize: '0.65rem' }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell sx={{ ...cell, fontWeight: 700, pl: 0.5 }}>{r.ticker}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                          <Typography sx={{ fontWeight: 800, fontFamily: 'monospace', fontSize: '0.72rem', color: qCfg.color }}>{r.quality_score}</Typography>
                          <Chip label={t(qCfg.key)} size="small" sx={{ fontSize: '0.52rem', fontWeight: 700, height: 16, bgcolor: qCfg.bg, color: qCfg.color }} />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...cell, color: '#64748b', fontSize: '0.68rem' }}>
                        {setupKey ? t(setupKey) : (r.setup_type || '—')}
                      </TableCell>
                      <TableCell sx={{ ...cell, color: '#64748b', fontSize: '0.65rem' }}>{typeof r.sector === 'object' ? (r.sector?.[lang] || '—') : (r.sector || '—')}</TableCell>
                      <TableCell align="right" sx={{ ...cell, fontWeight: 700, fontFamily: 'monospace', color: '#1e293b' }}>{r.close?.toLocaleString() || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : !loading && (
          <Box sx={{ textAlign: 'center', py: 3, color: '#94a3b8', fontSize: '0.78rem' }}>{t('commandCenter.detTable.empty')}</Box>
        )}
      </Box>

      {/* ── Cross-engine Consensus ── */}
      {consTop.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <SectionHeader
            icon={<Hub sx={{ fontSize: 18, color: '#6366f1' }} />}
            title={t('commandCenter.sections.consensus')} count={consTop.length} color="#6366f1"
            action={<Chip label={t('commandCenter.viewAll')} size="small" clickable onClick={() => navigate('/duplicate-signature')}
              sx={{ fontSize: '0.6rem', height: 20, color: '#6366f1', bgcolor: alpha('#6366f1', 0.06) }} />}
          />
          <TableContainer sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={hdr}>{t('commandCenter.consTable.ticker')}</TableCell>
                  <TableCell sx={hdr} align="center">{t('commandCenter.consTable.engines')}</TableCell>
                  <TableCell sx={hdr}>{t('commandCenter.consTable.consensus')}</TableCell>
                  <TableCell sx={hdr} align="right">{t('commandCenter.consTable.bestScore')}</TableCell>
                  <TableCell sx={hdr} align="right">{t('commandCenter.consTable.avgScore')}</TableCell>
                  <TableCell sx={hdr}>{t('commandCenter.consTable.sector')}</TableCell>
                  <TableCell sx={hdr} align="right">{t('commandCenter.consTable.price')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {consTop.slice(0, 10).map((r) => {
                  const cc = CONS_LABEL_CFG[r.consensus_label] || CONS_LABEL_CFG.WEAK;
                  return (
                    <TableRow key={r.ticker} hover sx={{ cursor: 'pointer' }} onClick={() => navigate('/duplicate-signature')}>
                      <TableCell sx={{ ...cell, fontWeight: 700 }}>{r.ticker}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', bgcolor: alpha(cc.color, 0.1), color: cc.color, fontSize: '0.78rem', fontWeight: 900 }}>
                          {r.num_engines}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={t(cc.key)} size="small" sx={{ fontSize: '0.58rem', fontWeight: 700, height: 18, bgcolor: alpha(cc.color, 0.08), color: cc.color }} />
                      </TableCell>
                      <TableCell align="right" sx={{ ...cell, fontWeight: 700, fontFamily: 'monospace', color: r.best_score >= 70 ? '#10b981' : '#64748b' }}>
                        {r.best_score.toFixed(1)}
                      </TableCell>
                      <TableCell align="right" sx={{ ...cell, fontFamily: 'monospace', color: '#64748b' }}>
                        {r.avg_score.toFixed(1)}
                      </TableCell>
                      <TableCell sx={{ ...cell, color: '#64748b', fontSize: '0.65rem' }}>{typeof r.sector === 'object' ? (r.sector?.[lang] || '—') : (r.sector || '—')}</TableCell>
                      <TableCell align="right" sx={{ ...cell, fontFamily: 'monospace' }}>{r.close?.toLocaleString() || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ── Sector Heatmap ── */}
      {sectors.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <SectionHeader
            icon={<TrendingUp sx={{ fontSize: 18, color: '#3b82f6' }} />}
            title={t('commandCenter.sections.sectorHeatmap')} count={sectors.length} color="#3b82f6"
          />
          <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
            {sectors.map((s) => {
              const qc = QUAD_CFG[s.quadrant] || QUAD_CFG.LAGGING;
              return (
                <Tooltip key={s.code} title={`${t('commandCenter.heatmap.opportunity')}${s.opportunity} / ${t('commandCenter.heatmap.risk')}${s.risk} / ${t('commandCenter.heatmap.priority')}${s.priority} / ${t('commandCenter.heatmap.stocks')}${s.num_stocks}`} arrow>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.4,
                    px: 1, py: 0.6, borderRadius: 2, minWidth: 100,
                    bgcolor: alpha(qc.color, 0.04), border: `1px solid ${alpha(qc.color, 0.12)}`,
                    transition: 'all 0.15s', '&:hover': { bgcolor: alpha(qc.color, 0.08) },
                  }}>
                    <FiberManualRecord sx={{ fontSize: 8, color: qc.color }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.1 }}>
                        {s.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.2 }}>
                        <Typography sx={{ fontSize: '0.55rem', color: qc.color, fontWeight: 600 }}>{t(qc.key)}</Typography>
                        <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8' }}>{t('commandCenter.heatmap.priorityShort')}{s.priority}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Quick nav */}
      {data && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          {[
            { label: t('commandCenter.nav.smScore'), path: '/smart-money-unified', color: '#10b981', icon: <Diamond sx={{ fontSize: 14 }} /> },
            { label: t('commandCenter.nav.consensus'), path: '/duplicate-signature', color: '#6366f1', icon: <Hub sx={{ fontSize: 14 }} /> },
            { label: t('commandCenter.nav.backtest'), path: '/backtest-validation', color: '#8b5cf6', icon: <Radar sx={{ fontSize: 14 }} /> },
            { label: t('commandCenter.nav.divergence'), path: '/market-divergence', color: '#f59e0b', icon: <TrendingUp sx={{ fontSize: 14 }} /> },
          ].map((nav) => (
            <Chip key={nav.path} icon={nav.icon} label={nav.label} size="small" clickable
              onClick={() => navigate(nav.path)}
              sx={{
                fontSize: '0.68rem', fontWeight: 600, height: 28,
                bgcolor: alpha(nav.color, 0.06), color: nav.color,
                border: `1px solid ${alpha(nav.color, 0.15)}`,
                '& .MuiChip-icon': { color: nav.color },
                '&:hover': { bgcolor: alpha(nav.color, 0.12) },
              }}
            />
          ))}
        </Box>
      )}

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack && <Alert severity={snack.severity} onClose={() => setSnack(null)} sx={{ fontSize: '0.78rem' }}>{snack.msg}</Alert>}
      </Snackbar>
    </Box>
  );
}
